import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, throwError } from 'rxjs';
import { ApiError } from './api-error';

/**
 * Global HTTP error interceptor.
 *
 * - Normalizes errors into a typed `ApiError`.
 * - Shows a Material snackbar for unexpected failures (5xx, network).
 * - Stays silent for 401/403 — those are surfaced by the page so it can
 *   trigger re-login or render a "forbidden" state. Keycloak auto-refresh
 *   already handles expired tokens.
 *
 * Backend is expected to return JSON like `{ message: "...", title: "..." }`
 * or an RFC 7807 `ProblemDetails`. We try several common shapes.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const snackBar = inject(MatSnackBar);

  return next(req).pipe(
    catchError((err: unknown) => {
      const apiError = toApiError(err);

      // Show snackbars only for the user-relevant cases. Auth issues are
      // handled by the route/guard layer, not by a toast.
      if (apiError.isServerError) {
        snackBar.open(
          `Server error (${apiError.status}). Please try again later.`,
          'Dismiss',
          { duration: 6000, panelClass: ['snack-error'] }
        );
      } else if (!apiError.isAuthError && apiError.status >= 400) {
        snackBar.open(apiError.message, 'Dismiss', {
          duration: 5000,
          panelClass: ['snack-error']
        });
      }

      return throwError(() => apiError);
    })
  );
};

function toApiError(err: unknown): ApiError {
  if (err instanceof HttpErrorResponse) {
    const status = err.status ?? 0;
    const message = extractMessage(err) ?? defaultMessage(status);
    return {
      status,
      message,
      isAuthError: status === 401 || status === 403,
      isServerError: status === 0 || status >= 500,
      cause: err
    };
  }

  return {
    status: 0,
    message:
      err instanceof Error ? err.message : 'An unexpected error occurred.',
    isAuthError: false,
    isServerError: true,
    cause: err
  };
}

function extractMessage(err: HttpErrorResponse): string | null {
  const body = err.error;
  if (!body) return null;
  if (typeof body === 'string') return body;
  if (typeof body === 'object') {
    const b = body as Record<string, unknown>;
    return (
      (typeof b['detail'] === 'string' && b['detail']) ||
      (typeof b['message'] === 'string' && b['message']) ||
      (typeof b['title'] === 'string' && b['title']) ||
      (typeof b['error'] === 'string' && b['error']) ||
      null
    );
  }
  return null;
}

function defaultMessage(status: number): string {
  switch (status) {
    case 0:
      return 'Network error — unable to reach the server.';
    case 400:
      return 'The request was invalid.';
    case 401:
      return 'Your session has expired. Please sign in again.';
    case 403:
      return "You don't have permission to perform this action.";
    case 404:
      return 'The requested resource was not found.';
    case 408:
      return 'The request timed out.';
    case 409:
      return 'There was a conflict with the current state.';
    case 422:
      return 'The request was rejected by the server.';
    case 429:
      return 'Too many requests. Please slow down.';
    default:
      if (status >= 500) return 'Server error. Please try again later.';
      return `Request failed with status ${status}.`;
  }
}
